import React, { createContext, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import leedpulse from '../configs/leedpulse';
import { auth } from '../configs/firebase';
import { IUser } from '@leedpulse-monorepo/interfaces';

export interface BackendContextType {
  version: string | null;
  refreshVersion: (bRefreshIfVersionChanged?: boolean) => Promise<{ old: string | null; new: string | null }>;
  postRequest: (endpoint: string, data: any, sApiKey: string) => Promise<any>;
  getRequest: (endpoint: string, headerParams: any) => Promise<any>;
  createUser: (userData: any, sApiKey: string) => Promise<any>;
  putUser: (userUid: string, newData: any, subscriptionApiKey: string, shouldUpdateCustomClaims?: boolean, callback?: ((data: any) => void)) => Promise<any>;
  removeSubscriptionToUser: (oUserToDelete: IUser, subscriptionId: string, subscriptionApiKey: string) => Promise<any>;
}

export const BackendContext = createContext<BackendContextType>({
  version: null,
  refreshVersion: async () => { return Promise.resolve({ old: null, new: null }); },
  postRequest: async () => { return Promise.resolve({}); },
  getRequest: async () => { return Promise.resolve({}); },
  createUser: async () => { return Promise.resolve({}); },
  putUser: async () => { return Promise.resolve({}); },
  removeSubscriptionToUser: async () => { return Promise.resolve({}); },
});

export const BackendProvider = ({ children }: { children: React.ReactNode }) => {
  const [version, setVersion] = useState<string | null>(null);

  /**
   * Get the version of the app and return old x new version to allow version checking
   * @param {boolean} bRefreshIfVersionChanged - If true, refresh the page if the version has changed
   * @returns {Object} { old: string, new: string }
   */
  const getRequest = async (endpoint: string, headerParams: any) => {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');

      const authData = {
        token: await auth.currentUser.getIdToken(),
        email: auth.currentUser.email,
      };

      const headers = {
        'Authorization': `Bearer ${authData.token}`,
        ...headerParams
      };

      const response = await axios.get(
        `${leedpulse.route_url}${endpoint}`,
        { headers }
      );

      return response.data;
    } catch (error: any) {
      console.error(`❌ Erreur API GET sur ${endpoint}:`, error.response ? error.response.data : error);
      throw error;
    }
  };

  const refreshVersion = useCallback(async (bRefreshIfVersionChanged = false) => {
    const oVersion: { old: string | null; new: string | null } = { old: version, new: null };
    if (!auth.currentUser) return oVersion;

    try {
      const oRes = await getRequest('/api/v1/info', null);
      const sBuildDate = oRes.buildDate;

      if (sBuildDate) {
        const sDate = sBuildDate.split('T')[0];
        const sTime = sBuildDate.split('T')[1];
        const sYear = sDate.split('-')[0].slice(2, 4);
        const sMonth = sDate.split('-')[1];
        const sDay = sDate.split('-')[2];
        const sHours = sTime.split(':')[0];
        const sMinutes = sTime.split(':')[1];
        const sVersion = `${sYear}${sMonth}${sDay}.${sHours}.${sMinutes}`
        oVersion.new = sVersion;

        if (bRefreshIfVersionChanged && oVersion.old !== null && oVersion.new !== version) window.location.reload();
        else setVersion(oVersion.new);
      }

      console.info("ℹ️ Current app version: ", oVersion);
      // Returned for test checking purpose only
      return oVersion;
    } catch (error) {
      console.error("Récupération de la version de l'application impossible:", error);
      return oVersion;
    }
  }, [version]);

  const postRequest = async (endpoint: string, data: any, sApiKey: string) => {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');

      const authData = {
        token: await auth.currentUser.getIdToken(),
        email: auth.currentUser.email,
      };

      // check if it is a FormData
      const isFormData = data instanceof FormData;

      const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        'Authorization': `Bearer ${authData.token}`,
        'x-api-key': sApiKey ?? "",
      };

      const response = await axios.post(
        `${leedpulse.route_url}${endpoint}`,
        data,
        { headers }
      );
      return response.data;
    } catch (error: any) {
      console.error(`❌ Erreur API POST sur ${endpoint}:`, error.response ? error.response.data : error);
      throw error;
    }
  };


  const removeSubscriptionToUser = async (oUserToDelete: IUser, subscriptionId: string, subscriptionApiKey: string) => {
    try {
      delete oUserToDelete.id_subscription[subscriptionId];
      return putUser(oUserToDelete.uid, { id_subscription: oUserToDelete.id_subscription }, subscriptionApiKey);

    } catch (error) {
      console.error('Error removing subscription from user in backend:', error);
      throw error;
    }
  };

  const putUser = async (userUid: string, newData: any, subscriptionApiKey: string, shouldUpdateCustomClaims = true, callback?: ((data: any) => void)) => {
    try {
      if (!auth.currentUser) throw new Error('User not authenticated');

      const authData = {
        token: await auth.currentUser.getIdToken(),
        email: auth.currentUser.email,
      };

      // Build headers
      const oHeaders = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authData.token}`,
        'x-api-key': subscriptionApiKey,
      };

      // Todo : add backend check to prevent a user to change supervisor role if unauthorized (or add this logic in backend but will conflict with backoffice changes)
      // Check if roles changed and user is "supervisor" -> keep supervisor role
      // @deprecated role supervisor removed
      // if("id_subscription" in newData) {
      //   // Get user current data (just before update) -> better to call here to prevent old front values to erase current values
      //   const userData = await getUsersByConstraints([{field: 'uid', op: '==', value: userUid}]);

      //   for(const idSub of Object.keys(newData.id_subscription)) {
      //     const prevRoles = userData.id_subscription[idSub]?.roles || [];
      //     const newRoles = newData.id_subscription[idSub]?.roles || [];
      //     if(prevRoles.some(role => role === UserType.SUPERVISOR) && !newRoles.some(role => role === UserType.SUPERVISOR)) {
      //       newRoles.push(UserType.SUPERVISOR);
      //     }
      //   }
      // }

      // Build body data
      const oBody = {
        uid: userUid,
        ...newData
      };

      if (!shouldUpdateCustomClaims) {
        oBody.shouldUpdateCustomClaims = false;
      }

      // Update user & return data
      const response = await axios.put(
        `${leedpulse.route_url}/api/v1/user`,
        oBody,
        {
          headers: oHeaders
        }
      );

      if (callback) {
        callback(response.data);
      }

      return response.data;
    } catch (error) {
      console.error('Error updating user in backend:', error);
      throw error;
    }
  }

  const createUser = async (userData: any, sApiKey: string) => {
    if (!auth.currentUser) throw new Error('User not authenticated');

    try {
      const authData = {
        token: await auth.currentUser.getIdToken(),
        email: auth.currentUser.email,
      };

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authData.token}`,
        'x-api-key': sApiKey,
        'x-creator-email': authData.email,
      };

      const response = await axios.post(`${leedpulse.route_url}/api/v1/users`, { user: userData, linked_subscription_id: userData.linked_subscription_id }, { headers });
      return response.data;
    } catch (error) {
      console.error('Error creating user in backend:', error);
      throw error;
    }
  };

  /**
   * Get the version of the app and set the version state
   */
  useEffect(() => {
    const unsubscribeAuthState = auth.onAuthStateChanged((user) => {
      if (user)
        refreshVersion();
    });

    return () => {
      unsubscribeAuthState();
    };
  }, [refreshVersion]);

  return (
    <BackendContext.Provider value={{
      version,
      refreshVersion,
      postRequest,
      getRequest,
      createUser,
      putUser,
      removeSubscriptionToUser,
    }}>
      {children}
    </BackendContext.Provider>
  );
};
